import {useCallback, useMemo} from 'react';
import {useRecoilValue} from 'recoil';
import {useGetNotificationsQuery, usePatchNotificationsMutation} from '../../../global/services/notifications';
import {authState} from '../../../store/auth/atoms';
import {
  filterNotificationsByTab,
  getActiveNotificationIds,
  getNewNotificationsCount,
  getNotificationIdsByMarkType,
} from '../helpers/filter-notifications';
import {mapNotificationDtos} from '../helpers/map-notification.dto';
import {selectSelectedTab} from '../selectors/notifications.selectors';
import {useNotificationsStore} from '../store/notifications.store';
import {NotificationMarkType} from '../types/notifications.types';

export const useNotificationsModel = () => {
  const {user} = useRecoilValue(authState);
  const selectedTab = useNotificationsStore(selectSelectedTab);
  const setSelectedTab = useNotificationsStore((state) => state.setSelectedTab);
  const userUid = user?.uid || '';
  const notificationsQuery = useGetNotificationsQuery(userUid);
  const refreshNotifications = notificationsQuery.refresh;
  const {mutate: patchNotifications, loading: patchLoading} = usePatchNotificationsMutation();

  const notifications = useMemo(() => mapNotificationDtos(notificationsQuery.data ?? []), [notificationsQuery.data]);

  const visibleNotifications = useMemo(
    () => filterNotificationsByTab(notifications, selectedTab),
    [notifications, selectedTab],
  );
  const newNotificationsCount = useMemo(() => getNewNotificationsCount(notifications), [notifications]);
  const newNotificationIds = useMemo(() => getNotificationIdsByMarkType(notifications, 'NEW'), [notifications]);
  const activeNotificationIds = useMemo(() => getActiveNotificationIds(notifications), [notifications]);

  const updateNotifications = useCallback(
    async (ids: string[], markType: NotificationMarkType) => {
      if (!ids.length) {
        return false;
      }

      let isUpdated = false;

      await patchNotifications(
        {
          uid: ids,
          mark_type: markType,
        },
        () => {
          isUpdated = true;
          refreshNotifications();
        },
      );

      return isUpdated;
    },
    [patchNotifications, refreshNotifications],
  );

  const updateNotification = useCallback(
    (id: string, markType: NotificationMarkType) => updateNotifications([id], markType),
    [updateNotifications],
  );

  return {
    notifications,
    visibleNotifications,
    selectedTab,
    setSelectedTab,
    loading: notificationsQuery.loading,
    error: notificationsQuery.error,
    refresh: refreshNotifications,
    patchLoading,
    newNotificationsCount,
    hasNotifications: activeNotificationIds.length > 0,
    canMarkAllAsRead: newNotificationIds.length > 0,
    updateNotification,
    markAllAsRead: () => updateNotifications(newNotificationIds, 'READ'),
    deleteAllNotifications: () => updateNotifications(activeNotificationIds, 'DELETE'),
  };
};
